import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Button, ErrorMessage, Icon, IconButton, Loading, TextInput, Tooltip, Typography } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useUpdateDriverEmailMutation } from "@/redux/apis/driver/driverProfileApi";
import { addToast, clsx } from "@/utils";
import { DriverDetail } from "../../../../common/DriverDetail";
import { useProfileContext } from "../../context";
import { EditAccountDetailsFormData, editAccountDetailsFormSchema } from "../../form";
import { UpdateDriverEmailModal } from "../../modals/UpdateDriverEmailModal";

export const AccountDetails = () => {
  const { driver, setActiveModal, activeSection, setActiveSection } = useProfileContext();
  const [updateDriverEmail, { isLoading }] = useUpdateDriverEmailMutation();
  const { personal } = driver;
  const [pendingEmail, setPendingEmail] = useState<string | undefined>(undefined);

  const {
    register,
    handleSubmit,
    formState: { isDirty, errors: formErrors },
  } = useForm<EditAccountDetailsFormData>({
    resolver: zodResolver(editAccountDetailsFormSchema),
    defaultValues: {
      email: personal.email,
    },
  });

  const handleEdit = () => {
    if (activeSection !== undefined) setActiveModal("save-changes");
    else setActiveSection("account-details");
  };

  const onSubmit = handleSubmit((data) => {
    if (editMode && isDirty) {
      setPendingEmail(data.email);
    }
  });

  const handleConfirmEmailUpdate = () => {
    if (!pendingEmail) return;
    updateDriverEmail({ id: driver.uuid, email: pendingEmail })
      .unwrap()
      .then(() => {
        addToast("success", "Successfully updated account details");
        setPendingEmail(undefined);
        setActiveSection(undefined);
      })
      .catch((e) => {
        getErrorMessages(e).forEach((message) => addToast("danger", message));
        setPendingEmail(undefined);
      });
  };

  const editMode = activeSection === "account-details";

  return (
    <>
      <form
        onSubmit={onSubmit}
        className="relative mt-4 flex flex-col rounded-lg border border-neutral-mid-gray bg-neutral-surface-gray p-5"
      >
        {isLoading && <Loading />}
        <div className="flex flex-row items-center">
          <Typography variant="title" className="flex-1">
            Account Details
          </Typography>
          {editMode ? (
            <div className="flex flex-row items-center gap-3">
              <Button size="sm" variant="secondary" onClick={() => setActiveSection(undefined)}>
                Cancel
              </Button>
              <Button size="sm" variant="primary" type="submit" disabled={!isDirty}>
                Save
              </Button>
            </div>
          ) : (
            <IconButton variant="tertiary" iconName="Edit2" size="sm" onClick={handleEdit} />
          )}
        </div>

        <div
          className={clsx(
            "mt-6 grid gap-6",
            editMode ? "grid-cols-1 md:grid-cols-2 2xl:grid-cols-3" : "grid-cols-1 md:grid-cols-2"
          )}
        >
          <DriverDetail
            label="Username / Login Email"
            detail={
              editMode ? (
                <div className="flex flex-col gap-1">
                  <TextInput hasError={!!formErrors.email} placeholder="Username / Login Email" {...register("email")} />
                  <ErrorMessage errors={formErrors} name="email" />
                </div>
              ) : (
                <div className="flex min-w-0 flex-col gap-1">
                  <Typography variant="paragraph" className="break-words">{personal.email}</Typography>
                  {personal.pendingEmail && (
                    <Typography variant="paragraph" className="break-all">
                      Pending: <span className="text-warning">{personal.pendingEmail}</span>
                      <Tooltip content="The driver will need to log in using this email to confirm the change." placement="right">
                        <Icon name="Danger" variant="Bold" size="md" className="ml-1 inline-block align-middle text-warning" />
                      </Tooltip>
                    </Typography>
                  )}
                </div>
              )
            }
          />

          <DriverDetail
            className={editMode ? "hidden" : ""}
            label="Password"
            detail={
              <Typography variant="paragraph" className="cursor-pointer text-info" onClick={() => setActiveModal("change-password")}>
                Change Password
              </Typography>
            }
          />
        </div>
      </form>

      <UpdateDriverEmailModal
        open={!!pendingEmail}
        isLoading={isLoading}
        onClose={() => setPendingEmail(undefined)}
        onContinue={handleConfirmEmailUpdate}
      />
    </>
  );
};
